import { useState } from "react";
import { previewProspectCsv } from "../../api/prospects";
import { Field, SelectField } from "./shared";
import styles from "./Admin.module.css";

export default function ProspectCsvImport({ onImport, onClose, onImported, onBusyChange, ready = true }) {
  const [source, setSource] = useState("");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState([]);
  const [keepExtra, setKeepExtra] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [result, setResult] = useState(null);

  const setWorking = (value) => { setBusy(value); onBusyChange?.(value); };
  const loadPreview = async (csv, columns, extras) => {
    const response = await previewProspectCsv({ csv, mapping: columns, keepExtra: extras });
    setPreview(response); setMapping(response.mapping); setDirty(false);
  };
  const selectFile = async (event) => {
    const file = event.target.files[0];
    setPreview(null); setResult(null); setSource(""); setError(""); setDirty(false);
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Choose a CSV file smaller than 5 MB."); return; }
    setWorking(true);
    try {
      const csv = await file.text();
      setSource(csv);
      await loadPreview(csv, undefined, keepExtra);
    } catch (err) { setError(err.message || "Could not preview CSV."); }
    finally { setWorking(false); }
  };
  const refreshPreview = async () => {
    setWorking(true); setError("");
    try { await loadPreview(source, mapping, keepExtra); }
    catch (err) { setError(err.message || "Could not preview CSV."); }
    finally { setWorking(false); }
  };
  const importRows = async () => {
    setWorking(true); setError("");
    try {
      const report = await onImport({ csv: source, mapping, keepExtra });
      setResult(report);
      onImported();
    } catch (err) {
      setError(`${err.message || "Could not import CSV."} If the connection was interrupted, close this panel and refresh the table before retrying to check whether the import completed.`);
    } finally { setWorking(false); }
  };

  return (
    <section className={styles.card} aria-label="Import prospects from CSV">
      <h2>Import CSV</h2>
      <p className={styles.dim}>
        Upload a UTF-8 CSV with headers (up to 5 MB / 5,000 rows / 200 columns).
        Missing fields stay blank; missing stages use To contact. Each row needs a business or contact name.
        Imports add new prospects. Uploading the same file again adds duplicates.
      </p>
      <p><a className={styles.linkBtn} download="sales-template.csv" href={`data:text/csv;charset=utf-8,${encodeURIComponent('Business name,Contact person,Phone,WhatsApp,Email,Location,Source,Service,Stage,Next follow-up,Notes\r\nExample Business,Sample Contact,0123456789,,,,Website,Website setup,To contact,,Call in the afternoon\r\n')}`}>Download CSV template</a></p>
      <fieldset className={styles.group} disabled={busy}>
        <Field label="CSV file" type="file" accept=".csv,text/csv" onChange={selectFile} />
      </fieldset>
      {busy && <p role="status">Processing CSV… Keep this tab open.</p>}
      {result && <p className={styles.success} role="status">{result.importedCount} imported · {result.invalidCount} skipped.</p>}
      {preview && !result && (
        <>
          <fieldset className={`${styles.group} ${styles.twoCol}`} disabled={busy}>
            {preview.headers.map((header, index) => (
              <SelectField key={index} label={`Column ${index + 1}: ${header}`} value={mapping[index]} onChange={(e) => { setMapping((previous) => previous.map((value, i) => i === index ? e.target.value : value)); setDirty(true); }}>
                <option value="">Extra column</option>
                {preview.fields.map(([key, label]) => <option key={key} value={key} disabled={mapping.some((value, i) => i !== index && value === key)}>{label}</option>)}
              </SelectField>
            ))}
            <label className={styles.checkbox}>
              <input type="checkbox" checked={keepExtra} onChange={(e) => { setKeepExtra(e.target.checked); setDirty(true); }} />
              Keep extra columns in Notes (uncheck to ignore)
            </label>
          </fieldset>
          {dirty ? <p role="status">Update the preview to validate your column changes.</p> : (
            <>
              <p role="status">{preview.validCount} ready · {preview.invalidCount} skipped.</p>
              {preview.invalidCount > 0 && (
                <details>
                  <summary>Review skipped rows ({preview.invalidCount})</summary>
                  <ul>{preview.errors.map((entry) => <li key={entry.row}>Data row {entry.row}: {entry.errors.join(" ")}</li>)}</ul>
                  {preview.invalidCount > 100 && <p>Showing the first 100 skipped rows.</p>}
                </details>
              )}
              <p className={styles.dim}>Preview of the first 5 valid prospects. Follow-up dates must use YYYY-MM-DD.</p>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead><tr><th>Data row</th><th>Business</th><th>Contact</th><th>Phone</th><th>Stage</th><th>Notes</th></tr></thead>
                  <tbody>{preview.preview.map(({ row, data }) => <tr key={row}><td>{row}</td><td>{data.businessName}</td><td>{data.contactName}</td><td>{data.phone}</td><td>{data.stage}</td><td className={styles.detailNotes}>{data.notes}</td></tr>)}</tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
      {error && <p className={styles.error} role="alert">{error}</p>}
      <div className={styles.formActions}>
        {dirty && preview && !result && <button className={styles.ghostBtn} disabled={busy} onClick={refreshPreview}>Update preview</button>}
        <button className={styles.primaryBtn} disabled={busy || !ready || dirty || !!result || !preview?.validCount} onClick={importRows}>{busy ? "Processing…" : `Import ${result ? 0 : preview?.validCount || 0} prospects`}</button>
        <button className={styles.ghostBtn} disabled={busy} onClick={onClose}>Close</button>
      </div>
    </section>
  );
}
